import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { getCardPrice } from '../utils/cardPricing';
import { EnergyIcon } from './icons/EnergyIcon'; 
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CardImage from './CardImage';
import { getOptimalImageUrl } from '../utils/formatImageUrls';

interface SimilarCardsProps {
  cards: Card[] | null;
  currentCardId: string;
}

const DRAG_THRESHOLD = 5;

export default function SimilarCards({ cards, currentCardId }: SimilarCardsProps) {
  const navigate = useNavigate();
  const { currency } = useCurrencyStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [visibleCardIndex, setVisibleCardIndex] = useState(0);

  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  // Preload all card images so scrolling feels smooth
  useEffect(() => {
    if (!cards?.length) return;
    cards.forEach(card => {
      const url = getOptimalImageUrl(card.images.small, { width: 200, quality: 75 });
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [cards]);

  if (!cards || !cards.length) return null;

  const handleCardClick = (card: Card, e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}/`);
  };

  const updateArrows = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    
    if (cards && cards.length) {
      const cardWidth = clientWidth / Math.min(5, cards.length);
      const newVisibleIndex = Math.floor(scrollLeft / cardWidth);
      setVisibleCardIndex(Math.min(newVisibleIndex, cards.length - 1));
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      updateArrows();
      scrollContainer.addEventListener('scroll', updateArrows);
      return () => scrollContainer.removeEventListener('scroll', updateArrows);
    }
  }, [cards]);

  const handleDragStart = (e: React.PointerEvent) => {
    if (!scrollContainerRef.current) return;
    e.preventDefault();
    document.body.classList.add('no-select');
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = scrollContainerRef.current.scrollLeft;
  };

  const handleDragEnd = () => {
    document.body.classList.remove('no-select');
    isDragging.current = false;
    // hasDragged stays true until next pointerdown so the click event is blocked
  };

  const handleDragMove = (e: React.PointerEvent) => {
    if (!isDragging.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > DRAG_THRESHOLD) {
      hasDragged.current = true;
    }
    scrollContainerRef.current.scrollLeft = dragScrollLeft.current - dx * 1.5;
  };

  const scrollPrev = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollNext = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
  };

  const getCardsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 2;
      if (window.innerWidth < 1024) return 3;
      return 5;
    }
    return 5;
  };
  
  const totalPages = Math.ceil(cards.length / getCardsPerView());

  useEffect(() => {
    if (!document.getElementById('no-select-style')) {
      const style = document.createElement('style');
      style.id = 'no-select-style';
      style.innerHTML = `
        .no-select, .no-select * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        .no-select::selection { background: transparent !important; }
        .no-select::-moz-selection { background: transparent !important; }
      `;
      document.head.appendChild(style);
    }
    return () => {
      document.body.classList.remove('no-select');
    };
  }, []);

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold mb-4">More {cards[0]?.name?.split(' ')[0]} Cards</h2>
      <div className="relative">
        {showLeftArrow && (
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-2 bg-black/80 rounded-full text-white/60 hover:text-white transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        
        {showRightArrow && (
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-2 bg-black/80 rounded-full text-white/60 hover:text-white transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight size={24} />
          </button>
        )}
        
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 px-4 select-none"
          onPointerDown={handleDragStart}
          onPointerUp={handleDragEnd}
          onPointerLeave={handleDragEnd}
          onPointerMove={handleDragMove}
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none', 
            WebkitUserSelect: 'none', 
            MozUserSelect: 'none', 
            userSelect: 'none',
            touchAction: 'pan-y',
            cursor: 'grab'
          }}
        >
          {cards.map(card => (
            <div
              key={card.id}
              className="flex-none w-[calc(50%-8px)] sm:w-[calc(33.333%-16px)] lg:w-[calc(20%-16px)] mr-4 snap-start"
            >
              <div
                className="group relative bg-white/5 border border-white/10 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/25 transition-all h-full"
                onClick={(e) => handleCardClick(card, e as React.MouseEvent)}
              >
                <div className="relative aspect-[2.5/3.5]">
                  <CardImage
                    src={card.images.small}
                    alt={card.name}
                    cardId={card.id}
                    className="absolute inset-0 w-full h-full object-cover"
                    width={200}
                    quality={75}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                    <div className="absolute bottom-0 inset-x-0 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <p className="text-sm font-medium text-white truncate">{card.name}</p>
                          <p className="text-xs text-white/60">
                            {card.number}/{card.set.printedTotal}
                          </p>
                        </div>
                        {card.types?.[0] && (
                          <EnergyIcon type={card.types[0]} />
                        )}
                      </div>
                      <p className="text-sm font-medium text-white/80">
                        {formatPrice(getCardPrice(card), currency)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (scrollContainerRef.current) {
                    const cardWidth = scrollContainerRef.current.clientWidth / getCardsPerView();
                    scrollContainerRef.current.scrollTo({
                      left: index * cardWidth * getCardsPerView(),
                      behavior: 'smooth'
                    });
                  }
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  Math.floor(visibleCardIndex / getCardsPerView()) === index ? 'bg-white' : 'bg-white/20'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
